import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    color: '#1a1a1a',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
    borderBottomWidth: 2,
    borderBottomColor: '#0066cc',
    paddingBottom: 20,
  },
  logo: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0066cc',
  },
  companyInfo: {
    textAlign: 'right',
    fontSize: 9,
    color: '#666',
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#1a1a1a',
  },
  subtitle: {
    fontSize: 12,
    color: '#666',
    marginBottom: 20,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 10,
    color: '#0066cc',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  row: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  label: {
    width: 120,
    color: '#666',
  },
  value: {
    flex: 1,
    fontWeight: 'bold',
  },
  table: {
    marginTop: 10,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
  },
  tableRow: {
    flexDirection: 'row',
    padding: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  tableCell: {
    flex: 1,
  },
  tableCellWide: {
    flex: 3,
  },
  tableCellRight: {
    flex: 1,
    textAlign: 'right',
  },
  tableCellHeader: {
    fontWeight: 'bold',
    fontSize: 9,
    textTransform: 'uppercase',
    color: '#666',
  },
  totalsSection: {
    marginTop: 20,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#ddd',
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: 5,
  },
  totalLabel: {
    width: 100,
    textAlign: 'right',
    paddingRight: 20,
    color: '#666',
  },
  totalValue: {
    width: 100,
    textAlign: 'right',
    fontWeight: 'bold',
  },
  grandTotal: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 2,
    borderTopColor: '#0066cc',
  },
  grandTotalLabel: {
    width: 100,
    textAlign: 'right',
    paddingRight: 20,
    fontSize: 14,
    fontWeight: 'bold',
  },
  grandTotalValue: {
    width: 100,
    textAlign: 'right',
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0066cc',
  },
  notes: {
    marginTop: 20,
    padding: 15,
    backgroundColor: '#f9f9f9',
    borderRadius: 4,
  },
  notesTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#666',
  },
  notesText: {
    fontSize: 9,
    color: '#444',
    lineHeight: 1.5,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 8,
    color: '#999',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    paddingTop: 10,
  },
  validityBadge: {
    backgroundColor: '#0066cc',
    color: 'white',
    padding: '4 8',
    borderRadius: 4,
    fontSize: 9,
  },
  statusBadge: {
    padding: '4 8',
    borderRadius: 4,
    fontSize: 9,
    fontWeight: 'bold',
  },
  infoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  infoBox: {
    width: '50%',
    marginBottom: 10,
  },
});

interface QuoteItem {
  id: string;
  itemType: string;
  name: string;
  description: string | null;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

interface QuotePDFProps {
  quote: {
    quoteNumber: string;
    status: string;
    version: number;
    travelStartDate: string | null;
    travelEndDate: string | null;
    validUntil: string;
    adults: number;
    children: number;
    infants: number;
    subtotal: number;
    discountAmount: number;
    discountPercent: number | null;
    taxAmount: number;
    totalAmount: number;
    currency: string;
    notes: string | null;
    termsConditions: string | null;
    createdAt: string;
    customer: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string | null;
      address?: string | null;
      city?: string | null;
      country?: string | null;
    };
    agent: {
      firstName: string;
      lastName: string;
      email: string;
    };
    items: QuoteItem[];
  };
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
}

function formatCurrency(amount: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(amount);
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function QuotePDF({
  quote,
  companyName = 'Polatkan Tourism',
  companyAddress = 'Istanbul, Turkey',
  companyPhone = '+90 XXX XXX XXXX',
  companyEmail = 'info@polatkan.com',
}: QuotePDFProps) {
  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.logo}>{companyName}</Text>
            <Text style={{ fontSize: 9, color: '#666', marginTop: 5 }}>
              Your Travel Partner
            </Text>
          </View>
          <View style={styles.companyInfo}>
            <Text>{companyAddress}</Text>
            <Text>{companyPhone}</Text>
            <Text>{companyEmail}</Text>
          </View>
        </View>

        {/* Quote Title */}
        <View style={{ marginBottom: 20 }}>
          <Text style={styles.title}>QUOTATION</Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
            <Text style={styles.subtitle}>
              {quote.quoteNumber} • Version {quote.version}
            </Text>
          </View>
        </View>

        {/* Customer & Quote Info */}
        <View style={{ flexDirection: 'row', marginBottom: 20 }}>
          <View style={{ flex: 1 }}>
            <Text style={styles.sectionTitle}>Customer Details</Text>
            <Text style={{ fontWeight: 'bold', marginBottom: 3 }}>
              {quote.customer.firstName} {quote.customer.lastName}
            </Text>
            <Text style={{ color: '#666', marginBottom: 2 }}>{quote.customer.email}</Text>
            {quote.customer.phone && (
              <Text style={{ color: '#666', marginBottom: 2 }}>{quote.customer.phone}</Text>
            )}
            {quote.customer.address && (
              <Text style={{ color: '#666', marginBottom: 2 }}>{quote.customer.address}</Text>
            )}
            {(quote.customer.city || quote.customer.country) && (
              <Text style={{ color: '#666' }}>
                {[quote.customer.city, quote.customer.country].filter(Boolean).join(', ')}
              </Text>
            )}
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.sectionTitle}>Quote Information</Text>
            <View style={styles.row}>
              <Text style={styles.label}>Date Issued:</Text>
              <Text style={styles.value}>{formatDate(quote.createdAt)}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Valid Until:</Text>
              <Text style={[styles.value, { color: '#cc0000' }]}>
                {formatDate(quote.validUntil)}
              </Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Agent:</Text>
              <Text style={styles.value}>
                {quote.agent.firstName} {quote.agent.lastName}
              </Text>
            </View>
          </View>
        </View>

        {/* Travel Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Travel Details</Text>
          <View style={styles.infoGrid}>
            <View style={styles.infoBox}>
              <View style={styles.row}>
                <Text style={styles.label}>Travel Dates:</Text>
                <Text style={styles.value}>
                  {quote.travelStartDate && quote.travelEndDate
                    ? `${formatDate(quote.travelStartDate)} - ${formatDate(quote.travelEndDate)}`
                    : 'To be confirmed'}
                </Text>
              </View>
            </View>
            <View style={styles.infoBox}>
              <View style={styles.row}>
                <Text style={styles.label}>Travelers:</Text>
                <Text style={styles.value}>
                  {quote.adults} Adult{quote.adults > 1 ? 's' : ''}
                  {quote.children > 0 && `, ${quote.children} Child${quote.children > 1 ? 'ren' : ''}`}
                  {quote.infants > 0 && `, ${quote.infants} Infant${quote.infants > 1 ? 's' : ''}`}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Line Items */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Services Included</Text>
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={[styles.tableCellWide, styles.tableCellHeader]}>Description</Text>
              <Text style={[styles.tableCell, styles.tableCellHeader]}>Type</Text>
              <Text style={[styles.tableCellRight, styles.tableCellHeader]}>Qty</Text>
              <Text style={[styles.tableCellRight, styles.tableCellHeader]}>Unit Price</Text>
              <Text style={[styles.tableCellRight, styles.tableCellHeader]}>Total</Text>
            </View>
            {quote.items.map((item) => (
              <View key={item.id} style={styles.tableRow}>
                <View style={styles.tableCellWide}>
                  <Text style={{ fontWeight: 'bold' }}>{item.name}</Text>
                  {item.description && (
                    <Text style={{ fontSize: 8, color: '#666', marginTop: 2 }}>
                      {item.description}
                    </Text>
                  )}
                </View>
                <Text style={styles.tableCell}>{item.itemType.replace('_', ' ')}</Text>
                <Text style={styles.tableCellRight}>{item.quantity}</Text>
                <Text style={styles.tableCellRight}>
                  {formatCurrency(item.unitPrice, quote.currency)}
                </Text>
                <Text style={styles.tableCellRight}>
                  {formatCurrency(item.totalPrice, quote.currency)}
                </Text>
              </View>
            ))}
          </View>

          {/* Totals */}
          <View style={styles.totalsSection}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Subtotal:</Text>
              <Text style={styles.totalValue}>
                {formatCurrency(quote.subtotal, quote.currency)}
              </Text>
            </View>
            {quote.discountAmount > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>
                  Discount{quote.discountPercent ? ` (${quote.discountPercent}%)` : ''}:
                </Text>
                <Text style={[styles.totalValue, { color: '#00aa00' }]}>
                  -{formatCurrency(quote.discountAmount, quote.currency)}
                </Text>
              </View>
            )}
            {quote.taxAmount > 0 && (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Tax:</Text>
                <Text style={styles.totalValue}>
                  {formatCurrency(quote.taxAmount, quote.currency)}
                </Text>
              </View>
            )}
            <View style={styles.grandTotal}>
              <Text style={styles.grandTotalLabel}>TOTAL:</Text>
              <Text style={styles.grandTotalValue}>
                {formatCurrency(quote.totalAmount, quote.currency)}
              </Text>
            </View>
          </View>
        </View>

        {/* Notes */}
        {quote.notes && (
          <View style={styles.notes}>
            <Text style={styles.notesTitle}>Notes</Text>
            <Text style={styles.notesText}>{quote.notes}</Text>
          </View>
        )}

        {/* Terms & Conditions */}
        {quote.termsConditions && (
          <View style={[styles.notes, { marginTop: 10 }]}>
            <Text style={styles.notesTitle}>Terms & Conditions</Text>
            <Text style={styles.notesText}>{quote.termsConditions}</Text>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text>
            Thank you for choosing {companyName}. This quote is valid until{' '}
            {formatDate(quote.validUntil)}.
          </Text>
          <Text style={{ marginTop: 5 }}>
            For questions, please contact {quote.agent.firstName} {quote.agent.lastName} at{' '}
            {quote.agent.email}
          </Text>
        </View>
      </Page>
    </Document>
  );
}
